import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ChevronRight, Layers, Folder } from "lucide-react";

function flatten(nodes, depth, acc) {
  for (const n of nodes) {
    acc.push({ ...n, depth });
    if (n.children && n.children.length) flatten(n.children, depth + 1, acc);
  }
  return acc;
}

export default function Categories() {
  const [tree, setTree] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => { api.get("/categories/tree").then((r) => setTree(r.data)).catch(() => setTree([])); }, []);

  const rows = useMemo(() => (tree ? flatten(tree, 0, []) : []), [tree]);

  const visible = useMemo(() => {
    const out = [];
    let hideUntilDepth = null;
    for (const r of rows) {
      if (hideUntilDepth !== null) {
        if (r.depth > hideUntilDepth) continue;
        hideUntilDepth = null;
      }
      out.push(r);
      if (collapsed[r.id]) hideUntilDepth = r.depth;
    }
    return out;
  }, [rows, collapsed]);

  const hasChildren = (id) => rows.some((r) => r.parent_id === id);

  if (!tree) return <div className="flex justify-center h-96 items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-white tracking-tight">Categories</h1>
        <p className="text-slate-400 mt-2">A dynamic hierarchy that grows and merges automatically as new material is understood.</p>
      </div>
      {tree.length === 0 ? (
        <div className="rounded-xl border border-white/5 bg-[#111827] p-16 text-center text-slate-500">
          <Layers className="w-10 h-10 mx-auto mb-4 text-slate-700" /> No categories yet.
        </div>
      ) : (
        <div className="rounded-xl border border-white/5 bg-[#111827] p-3 max-w-3xl">
          {visible.map((n) => {
            const kids = hasChildren(n.id);
            return (
              <div key={n.id} className="flex items-center gap-1 group" style={{ paddingLeft: n.depth * 18 }}>
                {kids ? (
                  <button onClick={() => setCollapsed((c) => ({ ...c, [n.id]: !c[n.id] }))} data-testid={`toggle-${n.id}`} className="p-1 text-slate-500 hover:text-white">
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${collapsed[n.id] ? "" : "rotate-90"}`} />
                  </button>
                ) : <span className="w-[22px]" />}
                <Link to={`/category/${n.id}`} data-testid={`category-node-${n.id}`} className="flex-1 flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-white/5 transition-colors">
                  <span className="flex items-center gap-2 text-sm text-slate-300 group-hover:text-white">
                    <Folder className="w-3.5 h-3.5 text-blue-400/70" />
                    {n.name}
                    <span className="text-[10px] uppercase tracking-wide text-slate-600">{n.type}</span>
                  </span>
                  <span className="text-xs text-slate-600 font-mono">{n.file_count}</span>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
